/**
 * navGuard — lets ONE screen hold the student on the page until they confirm leaving (Buddy Call:
 * "This will end your call with Veda"). wouter 3 has no blocker, so this adds two hooks:
 *
 *  1. In-app navigation: `guardedAroundNav` is passed to `<Router aroundNav>` in App.jsx. Every
 *     `navigate()` and `<Redirect>` goes through it. A blocked one is handed to the blocker's
 *     `onBlocked(path, proceed)`; `proceed()` performs it later, unguarded.
 *  2. Back / Forward: a CAPTURE-phase `popstate` listener on window runs before wouter's
 *     listener; for a blocked target it stops the event (wouter never renders the destination),
 *     moves history back to where it was with `history.go(-delta)`, and hands over `proceed`,
 *     which replays the move. The delta comes from an index stamped into every entry's
 *     `history.state` (`aroundNav` stamps pushes; `installNavGuard` stamps the first entry).
 *
 * With no blocker set, both paths behave exactly like plain wouter. An entry without an index
 * (pushed by code outside wouter) is never blocked — we couldn't put it back.
 *
 * Tab close / reload can't be guarded here; screens use `beforeunload` themselves.
 */

const IDX = 'wysoNavIdx';

/** @type {{ shouldBlock: (path: string) => boolean, onBlocked: (path: string, proceed: () => void) => void } | null} */
let blocker = null;
let curIdx = 0;
let ignoreNextPop = false;
let allowNextPop = false;
let installed = false;

const pathOf = (to) => String(to).split(/[?#]/)[0];
const idxOf = (state) => (Number.isInteger(state?.[IDX]) ? state[IDX] : null);

/** Set (or clear with null) the one active blocker. */
export function setNavBlocker(next) {
  blocker = next;
}

/** wouter `aroundNav`: stamps the history index and consults the blocker. */
export function guardedAroundNav(navigate, to, options = {}) {
  const go = () => {
    const idx = options.replace ? curIdx : curIdx + 1;
    curIdx = idx;
    navigate(to, { ...options, state: { ...(options.state || {}), [IDX]: idx } });
  };
  const path = pathOf(to);
  if (blocker && blocker.shouldBlock(path)) {
    blocker.onBlocked(path, go);
    return;
  }
  go();
}

function onPopState(event) {
  const idx = idxOf(event.state);
  if (ignoreNextPop) {
    ignoreNextPop = false;
    // The revert we caused lands back on the rendered entry: wouter must not see it either.
    // Anything else (the revert never happened) is handled as an ordinary pop below.
    if (idx === curIdx) {
      event.stopImmediatePropagation();
      return;
    }
  }
  if (allowNextPop) {
    allowNextPop = false;
  } else if (blocker && idx !== null && idx !== curIdx && blocker.shouldBlock(window.location.pathname)) {
    event.stopImmediatePropagation();
    const delta = idx - curIdx;
    const path = window.location.pathname;
    ignoreNextPop = true;
    window.history.go(-delta);
    blocker.onBlocked(path, () => {
      allowNextPop = true;
      window.history.go(delta);
    });
    return;
  }
  if (idx !== null) curIdx = idx;
}

/** Call once at startup (App.jsx). Idempotent; a no-op without a window. */
export function installNavGuard() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const existing = idxOf(window.history.state);
  if (existing === null) window.history.replaceState({ ...(window.history.state || {}), [IDX]: 0 }, '');
  curIdx = existing ?? 0;
  window.addEventListener('popstate', onPopState, true);
}

/** Tests only: forget all module state and remove the listener. */
export function resetNavGuardForTests() {
  if (typeof window !== 'undefined') window.removeEventListener('popstate', onPopState, true);
  blocker = null;
  curIdx = 0;
  ignoreNextPop = false;
  allowNextPop = false;
  installed = false;
}
